<template>
  <InstructorLayout>
    <div class="instructor-dashboard">
      <div class="dashboard-header">
      <div class="header-actions">
        <!-- ビューモード切り替え -->
        <div class="view-mode-toggle">
          <button 
            @click="setViewMode('submissions')" 
            class="btn btn-mode" 
            :class="{ active: viewMode === 'submissions' }"
          >
            📚 課題別
          </button>
          <button 
            @click="setViewMode('students')" 
            class="btn btn-mode" 
            :class="{ active: viewMode === 'students' }"
          >
            👥 生徒別
          </button>
        </div>
        
        <!-- Buddy System制御 -->
        <div class="buddy-toggles">
          <label class="toggle">
            <input type="checkbox" v-model="helpOn" :disabled="!canCloseModal" @change="onScopeToggle" />
            <span>🚨 HELPする</span>
          </label>
          <label class="toggle">
            <input type="checkbox" v-model="showPool" :disabled="!canCloseModal" @change="onScopeToggle" />
            <span>🏊 Pool表示</span>
          </label>
        </div>
        
        <button @click="refreshData" class="btn btn-secondary" :disabled="isLoading">
          🔄 更新
        </button>
        <button @click="goToNotificationSettings" class="btn btn-info">
          🔔 通知設定
        </button>

      </div>
    </div>

    <!-- 統計情報（固定表示） -->
    <div class="stats-fixed">
      <div class="stat-card-fixed">
        <div class="stat-number">{{ pendingFeedback }}</div>
        <div class="stat-label">FB待ち</div>
      </div>
    </div>

        <!-- フィルター・検索（スマホ最適化） -->
    <div class="filters-section">
      <!-- フィルター選択（横並び） -->
      <div class="filter-row">
        <!-- フィルター種類選択 -->
        <div class="filter-type-selector">
          <select id="filterType" v-model="selectedFilterType" class="form-select">
            <option value="">フィルターを選択</option>
            <option value="day">Day</option>
            <option value="student">生徒名</option>
          </select>
        </div>

        <!-- ステータス選択（常時表示） -->
        <div class="status-selector">
          <select v-model="statusFilter" class="form-select" @change="applyFilters">
            <option value="">ステータス選択</option>
            <option value="submitted">FB待ち</option>
            <option value="feedback_completed">フィードバック完了</option>
            <option value="no_reply_needed">返信不要</option>
          </select>
        </div>

        <!-- フィルター解除ボタン -->
        <div class="clear-filter-btn">
          <button @click="clearAllFilters" class="btn btn-clear">
            解除
          </button>
        </div>
      </div>

      <!-- 選択されたフィルターの値入力 -->
      <div v-if="selectedFilterType" class="filter-value-selector">
        <!-- Dayフィルター -->
        <div v-if="selectedFilterType === 'day'" class="filter-input-group">
          <select v-model="dayFilter" class="form-select" @change="applyFilters">
            <option value="">すべて</option>
            <option v-for="day in 180" :key="day" :value="day">Day {{ day }}</option>
          </select>
        </div>

        <!-- 生徒名検索 -->
        <div v-if="selectedFilterType === 'student'" class="filter-input-group">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="生徒名を入力..."
            class="search-input"
            @input="applyFilters"
          />
        </div>
      </div>
    </div>



    <!-- 提出課題一覧（課題別ビュー） -->
    <div v-if="viewMode === 'submissions'" class="submissions-section">
      <h2>📚 提出課題一覧</h2>
      
      <!-- Pool選択時の表示情報 -->
      <div v-if="showPool && filteredSubmissions.length > 100" class="pool-info">
        <p class="info-text">
          🏊 Pool表示: {{ displaySubmissions.length }}件 / {{ filteredSubmissions.length }}件
          （優先度順に上位100件を表示）
        </p>
      </div>
      
      <div v-if="isLoading" class="loading">
        <p>データを読み込み中...</p>
      </div>

      <div v-else-if="displaySubmissions.length === 0" class="no-data">
        <p>該当する提出課題がありません</p>
      </div>

      <div v-else class="submissions-list">
        <div
          v-for="submission in displaySubmissions"
          :key="submission.id"
          class="submission-card"
          :class="{ 'needs-feedback': submission.status === 'submitted' }"
        >
          <div class="submission-header">
            <div class="student-info">
              <div class="student-name-section">
                <h3>{{ submission.student_name || '未設定' }}</h3>
              </div>
            </div>
            <div class="submission-meta">
              <span class="day-badge">Day {{ submission.day }}</span>
              <span class="type-badge" :class="submission.type">
                {{ submission.type === 'practice' ? '練習' : '課題' }}
              </span>
              <span class="status-badge" :class="submission.status">
                {{ getStatusText(submission.status) }}
              </span>
              <!-- Assigned/Buddy/Poolバッジ -->
              <span v-if="submission.__badge_assigned" class="badge-role assigned">担当</span>
              <span v-if="submission.__badge_buddy" class="badge-role buddy">Buddy</span>
              <span v-if="submission.__badge_pool" class="badge-role pool">Pool</span>
            </div>
            <div class="submission-header-right">
              <button 
                @click="openFullScreenFeedback(submission)" 
                class="btn btn-fb"
              >
                FBを行う
              </button>
            </div>
          </div>

          <div class="submission-details">
            <div class="detail-row">
              <span class="detail-label">録音時間:</span>
              <span class="detail-value">{{ formatDuration(submission.duration) }}</span>
            </div>
            <div class="detail-row practice-info-row">
              <span class="detail-label">練習:</span>
              <span class="detail-value practice-indicator" :class="{ 'has-practice': submission.__has_practice, 'no-practice': !submission.__has_practice }">
                <template v-if="submission.__has_practice">
                  {{ submission.__practice_count }}回 (合計 {{ formatDuration(submission.__practice_total_duration) }})
                </template>
                <template v-else>
                  未実施
                </template>
              </span>
            </div>
            <div class="detail-row">
              <span class="detail-label">提出日時:</span>
              <span class="detail-value">{{ formatDateTime(submission.created_at) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">経過時間:</span>
              <span class="detail-value">{{ getElapsedTime(submission.created_at) }}</span>
            </div>
            <div v-if="submission.status === 'submitted'" class="detail-row">
              <span class="detail-label">14:00締切:</span>
              <span class="detail-value deadline-indicator" 
                    :class="getDeadlineRemaining(submission.created_at)?.cssClass">
                {{ getDeadlineRemaining(submission.created_at)?.text }}
              </span>
            </div>
          </div>


          <div class="submission-actions">
            
            <!-- フィードバック開始ボタンを非表示（FBを行うボタンに統合） -->
            <!-- <button 
              v-if="submission.status === 'submitted'"
              @click="startFeedback(submission)" 
              class="btn btn-success small"
            >
              📝 フィードバック開始
            </button> -->
            
            
                         <button 
               v-if="submission.status === 'feedback_completed'"
               @click="viewFeedback(submission)" 
               class="btn btn-info small"
             >
               👁️ フィードバック確認
             </button>
             
             <button 
               v-if="submission.status === 'submitted'"
               @click="markAsNoReply(submission)" 
               class="btn btn-warning small"
             >
               🚫 返信不要
             </button>
             
             <button 
               v-if="submission.status === 'no_reply_needed'"
               @click="unmarkAsNoReply(submission)" 
               class="btn btn-info small"
             >
               🔄 返信不要解除
             </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 生徒別ビュー -->
    <div v-if="viewMode === 'students'" class="students-section">
      <h2>👥 生徒別提出履歴</h2>
      
      <div v-if="isLoading" class="loading">
        <p>データを読み込み中...</p>
      </div>

      <div v-else-if="studentsData.length === 0" class="no-data">
        <p>生徒データがありません</p>
      </div>

      <div v-else class="students-list">
        <div
          v-for="student in studentsData"
          :key="student.user_id"
          class="student-card"
        >
          <!-- 生徒ヘッダー（第1階層） -->
          <div class="student-header" @click="toggleStudent(student.user_id)">
            <div class="student-info">
              <h3>{{ student.student_name }}</h3>
              <div class="student-status">
                <span v-if="loadingPracticeForStudent.has(student.user_id)" class="loading-badge">
                  ⏳ 読み込み中...
                </span>
                <span v-if="student.unreadCount > 0" class="unread-badge">
                  📝 {{ student.unreadCount }}件未返信
                </span>
                <span v-if="student.oldestUnreadTime" 
                      class="deadline-badge small"
                      :class="getDeadlineRemaining(student.oldestUnreadTime)?.isOverdue ? 'overdue' : 'safe'">
                  ⏰ {{ getFeedbackDeadline(student.oldestUnreadTime) }}
                </span>
              </div>
            </div>
            <div class="student-toggle">
              <span class="toggle-icon">{{ expandedStudents.includes(student.user_id) ? '▼' : '▶' }}</span>
            </div>
          </div>

          <!-- 生徒の180日分提出履歴（第2階層） -->
          <div v-if="expandedStudents.includes(student.user_id)" class="student-days">
            <div class="days-list">
              <div
                v-for="dayData in student.daysData"
                :key="dayData.day"
                class="day-card"
                :class="{ 'has-unread': dayData.hasUnread }"
              >
                <!-- Dayヘッダー（第2階層） -->
                <div class="day-header" @click="toggleDay(student.user_id, dayData.day)">
                  <div class="day-info">
                    <span class="day-number">Day {{ dayData.day }}</span>
                    <div class="day-status">
                      <span v-if="dayData.unreadCount > 0" class="unread-badge small">
                        📝 {{ dayData.unreadCount }}件
                      </span>
                      <span v-if="dayData.oldestUnreadTime" 
                            class="deadline-badge small"
                            :class="getDeadlineRemaining(dayData.oldestUnreadTime)?.isOverdue ? 'overdue' : 'safe'">
                        ⏰ {{ getFeedbackDeadline(dayData.oldestUnreadTime) }}
                      </span>
                    </div>
                  </div>
                  <div class="day-toggle">
                    <span class="toggle-icon">{{ expandedDays.includes(`${student.user_id}-${dayData.day}`) ? '▼' : '▶' }}</span>
                  </div>
                </div>

                <!-- Dayの詳細（第3階層） -->
                <div v-if="expandedDays.includes(`${student.user_id}-${dayData.day}`)" class="day-details">
                  <div class="submissions-detail">
                    <!-- 練習 -->
                    <div class="submission-detail practice">
                      <div class="submission-header">
                        <span class="type-label">練習</span>
                        <span v-if="dayData.practiceSubmissions.length > 0" class="submission-count">
                          ({{ dayData.practiceSubmissions.length }}件)
                        </span>
                      </div>
                      <div v-if="dayData.practiceSubmissions.length > 0" class="submissions-list">
                        <div 
                          v-for="(submission, index) in dayData.practiceSubmissions" 
                          :key="`practice-${submission.id}`"
                          class="submission-item"
                        >
                          <div class="submission-item-header">
                            <span class="submission-number">#{{ index + 1 }}</span>
                            <span class="status" :class="submission.status">
                              {{ getStatusText(submission.status) }}
                            </span>
                          </div>
                          <div class="submission-item-info">
                            <span class="info-item">録音時間: {{ formatDuration(submission.duration) }}</span>
                            <span class="info-item">提出日時: {{ formatDateTime(submission.created_at) }}</span>
                          </div>
                          
                          <div class="submission-item-actions">
                            <button 
                              @click="openFullScreenFeedback(submission)" 
                              class="btn btn-fb small"
                            >
                              FBを行う
                            </button>
                          </div>
                        </div>
                      </div>
                      <div v-else class="no-submissions">
                        <span class="status not-submitted">未提出</span>
                      </div>
                    </div>
                    
                    <!-- 課題 -->
                    <div class="submission-detail assignment">
                      <div class="submission-header">
                        <span class="type-label">課題</span>
                        <span v-if="dayData.assignmentSubmissions.length > 0" class="submission-count">
                          ({{ dayData.assignmentSubmissions.length }}件)
                        </span>
                      </div>
                      <div v-if="dayData.assignmentSubmissions.length > 0" class="submissions-list">
                        <div 
                          v-for="(submission, index) in dayData.assignmentSubmissions" 
                          :key="`assignment-${submission.id}`"
                          class="submission-item"
                        >
                          <div class="submission-item-header">
                            <span class="submission-number">#{{ index + 1 }}</span>
                            <span class="status" :class="submission.status">
                              {{ getStatusText(submission.status) }}
                            </span>
                          </div>
                          <div class="submission-item-info">
                            <span class="info-item">録音時間: {{ formatDuration(submission.duration) }}</span>
                            <span class="info-item">提出日時: {{ formatDateTime(submission.created_at) }}</span>
                            <span class="info-item practice-indicator-inline" :class="{ 'has-practice': submission.__has_practice, 'no-practice': !submission.__has_practice }">
                              練習: 
                              <template v-if="submission.__has_practice">
                                {{ submission.__practice_count }}回 ({{ formatDuration(submission.__practice_total_duration) }})
                              </template>
                              <template v-else>
                                未実施
                              </template>
                            </span>
                          </div>
                          

                          <div class="submission-item-actions">
                             <!-- フィードバック開始ボタンを非表示（FBを行うボタンに統合） -->
                             <!-- <button 
                               v-if="submission.status === 'submitted'"
                               @click="startFeedback(submission)" 
                               class="btn btn-success small"
                             >
                               📝 フィードバック開始
                             </button> -->
                             
                             <button 
                               @click="openFullScreenFeedback(submission)" 
                               class="btn btn-fb small"
                             >
                               FBを行う
                             </button>
                             
                                                           <button 
                                v-if="submission.status === 'submitted'"
                                @click="markAsNoReply(submission)" 
                                class="btn btn-warning small"
                              >
                                🚫 返信不要
                              </button>
                              
                              <button 
                                v-if="submission.status === 'no_reply_needed'"
                                @click="unmarkAsNoReply(submission)" 
                                class="btn btn-info small"
                              >
                                🔄 返信不要解除
                              </button>
                            </div>
                          </div>
                        </div>
                        <div v-else class="no-submissions">
                          <span class="status not-submitted">未提出</span>
                        </div>
                      </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- フィードバック録音モーダル -->
    <div v-if="showFeedbackModal" class="feedback-modal" 
         @click.self="canCloseModal ? closeFeedbackModal() : null">
      <div class="modal-content">
        <h3>📝 フィードバック録音</h3>
        <div class="feedback-info">
          <p><strong>生徒:</strong> {{ currentSubmission?.student_name || '未設定' }}</p>
          <p><strong>Day {{ currentSubmission?.day }} {{ currentSubmission?.type === 'practice' ? '練習' : '課題' }}</strong></p>
        </div>

        <div class="recording-section">
          <div v-if="canShowStartButton && !isPreviewReady" class="recording-start">
            <div class="media-kind-toggle">
              <label>
                <input type="radio" value="audio" v-model="selectedMediaKind" />
                🎙️ 音声
              </label>
              <label :class="{ 'is-disabled': !isVideoSupported }">
                <input type="radio" value="video" v-model="selectedMediaKind" :disabled="!isVideoSupported" />
                🎬 動画
              </label>
              <p v-if="!isVideoSupported" class="media-kind-note">この端末では動画フィードバックに対応していません（Android/PCのみ対応）</p>
            </div>
            <button
              v-if="selectedMediaKind === 'video'"
              @click="onPrepareVideoPreview"
              class="btn btn-primary large"
              :disabled="!canStartNow || isPreparingPreview"
            >
              {{ isPreparingPreview ? '📷 カメラ準備中...' : '🎬 カメラ準備' }}
            </button>
            <button v-else @click="startRecording" class="btn btn-primary large" :disabled="!canStartNow">
              🎙️ 録音開始
            </button>
          </div>

          <!-- 動画: 録画開始前のプレビュー確認・向き補正フェーズ -->
          <div v-if="isPreviewReady && !isRecording" class="video-preview-section">
            <div class="preview-outer">
              <video
                :ref="setPreviewVideoEl"
                class="recording-preview"
                :style="previewVideoStyle"
                autoplay
                muted
                playsinline
              ></video>
            </div>
            <button @click="onRotatePreview" class="btn btn-secondary rotate-btn">↻ 向きを直す</button>
            <div class="preview-actions">
              <button @click="onStartVideoRecording" class="btn btn-primary large">🎬 録画開始</button>
              <button @click="onCancelVideoPreview" class="btn btn-secondary">キャンセル</button>
            </div>
          </div>

          <div v-if="isRecording" class="recording-active">
            <div v-if="selectedMediaKind === 'video'" class="preview-outer">
              <video
                :ref="setPreviewVideoEl"
                class="recording-preview"
                :style="previewVideoStyle"
                autoplay
                muted
                playsinline
              ></video>
            </div>
            <div class="recording-indicator">
              <span class="recording-dot"></span>
              <span>録音中...</span>
            </div>
            <div class="recording-time">{{ formatRecordingTime(recordingTime) }}</div>
            <button
              @click="stopRecording"
              class="btn btn-danger"
              :disabled="!canStopRecording"
              :class="{ 'btn-disabled': !canStopRecording }"
            >
              {{ canStopRecording ? '⏹️ 録音停止' : `⏹️ 録音停止 (${Math.max(0, 3 - recordingTime)}秒後)` }}
            </button>
          </div>

          <!-- アップロード進捗表示 -->
          <div v-if="isUploading" class="upload-progress-section">
            <div class="upload-indicator">
              <span class="upload-spinner">📤</span>
              <span>アップロード中...</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: uploadProgress.percentage + '%' }"></div>
            </div>
            <div class="progress-text">
              {{ uploadProgress.completed }} / {{ uploadProgress.total }} チャンク
            </div>
          </div>

          <!-- 録音完了後の音声プレイヤーと操作ボタン -->
          <div v-if="isCompleted && !isRecording && !isUploading" class="recording-completed">
            <div class="completed-message">
              <span class="success-icon">✅</span>
              <span>録音が完了しました</span>
            </div>
            
            <!-- 音声/動画プレイヤー（自己確認用） -->
            <div v-if="finalAudioUrl" class="audio-player-section">
              <video
                v-if="selectedMediaKind === 'video'"
                :src="finalAudioUrl"
                controls
                playsinline
                preload="metadata"
                class="feedback-video-player"
                controlslist="nodownload noplaybackrate"
              >
                お使いのブラウザは動画再生に対応していません。
              </video>
              <audio
                v-else
                :src="finalAudioUrl"
                controls
                preload="metadata"
                class="feedback-audio-player"
                controlslist="nodownload noplaybackrate"
              >
                お使いのブラウザは音声再生に対応していません。
              </audio>
            </div>
            
            <div class="recording-actions">
              <button 
                @click="retakeRecording" 
                class="btn btn-secondary"
                :disabled="isDeleting"
              >
                {{ isDeleting ? '🗑️ 削除中...' : '🔄 撮り直し' }}
              </button>
              <button @click="handleSendFeedback" class="btn btn-success" :disabled="isDeleting">
                📤 送信
              </button>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button @click="closeFeedbackModal" class="btn btn-secondary" 
                  :disabled="!canCloseModal"
                  :aria-disabled="!canCloseModal"
                  :tabindex="!canCloseModal ? -1 : 0">
            {{ canCloseModal ? 'キャンセル' : '操作中...' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 全画面フィードバックモーダル -->
    <Transition name="fullscreen-modal">
      <div v-if="showFullScreenFeedback" class="fullscreen-feedback-modal" @click.self="canCloseModal ? closeFullScreenFeedback() : null">
        <div class="fullscreen-content">
          <!-- 閉じるボタン -->
          <button @click="closeFullScreenFeedback" class="close-btn" :disabled="!canCloseModal" :aria-disabled="!canCloseModal" :tabindex="!canCloseModal ? -1 : 0" :title="canCloseModal ? '閉じる' : '操作中のため閉じられません'">
            {{ canCloseModal ? '✕' : '⏳' }}
          </button>
          
          <!-- フィードバック画面の内容 -->
          <div class="feedback-screen">
            <div class="feedback-header-mobile">
              <div class="student-name-line">{{ currentFullScreenSubmission?.student_name || '未設定' }}</div>
              <div v-if="currentFullScreenSubmission?.student_name_kana" class="student-kana-line">{{ currentFullScreenSubmission.student_name_kana }}</div>
              <div class="assignment-line">Day {{ currentFullScreenSubmission?.day }} {{ currentFullScreenSubmission?.type === 'practice' ? '練習' : '課題' }}</div>
            </div>
            
            <!-- フィードバック機能セクション -->
            <div class="feedback-sections">
              
              <!-- 1. フィードバックマニュアル -->
              <div class="feedback-section feedback-manual-section">
                <h3 class="manual-title">📖 フィードバックの手順</h3>
                <div class="manual-content">
                  <div class="manual-steps">
                    <p>1.（下の名前）さんへ、<br>Day△△お疲れさまでした！</p>
                    <p>2.今日の良かったところは・・・。<br>（最初に良かった点を1つ以上伝える）</p>
                    <p>3.今日の復習は、〇〇の部分です。<br>△△を意識して3回、読んでみましょう！</p>
                    <p>4.（あれば一言加えて、）明日の提出も楽しみにしています！</p>
                  </div>
                </div>
              </div>

              <!-- 2. Day課題内容表示 -->
              <div class="feedback-section feedback-assignment-section">
                <h3>📋 Day {{ currentFullScreenSubmission?.day }} 課題内容</h3>
                <div class="assignment-content">
                  <p><strong>課題タイトル:</strong> Day {{ currentFullScreenSubmission?.day }} の発音練習</p>
                  <p><strong>練習内容:</strong> 指定された英単語・フレーズの発音練習</p>
                  <p><strong>評価ポイント:</strong> 発音の正確性、イントネーション、流暢性</p>
                </div>
              </div>

              <!-- 音声・録音セクション（固定表示用） -->
              <div class="feedback-controls-section">
                <!-- 提出音源再生（最小化） -->
                <div class="feedback-section feedback-audio-section">
                  <div class="submitted-audio-section">
                    <div class="audio-slot">
                      <button
                        v-if="!players[currentFullScreenSubmission?.id]"
                        class="btn btn-primary"
                        @click="prefetchPlaybackUrl(currentFullScreenSubmission?.id)"
                        :disabled="loadingId === currentFullScreenSubmission?.id"
                      >
                        {{ loadingId === currentFullScreenSubmission?.id ? '準備中…' : '🔗 URLを再取得' }}
                      </button>

                      <!-- プレイヤーのみ表示 -->
                      <div v-else class="audio-player-section">
                        <audio
                          :ref="setAudioRef(currentFullScreenSubmission?.id)"
                          :src="players[currentFullScreenSubmission?.id]?.url"
                          controls
                          preload="metadata"
                          playsinline
                          class="fullscreen-audio-player"
                          controlslist="nodownload noplaybackrate"
                          @play="ensureTimer(currentFullScreenSubmission?.id)"
                          @stalled="refreshUrl(currentFullScreenSubmission?.id, true)"
                          @error="refreshUrl(currentFullScreenSubmission?.id, true)"
                          @seeking="maybeSeekRefresh(currentFullScreenSubmission?.id)"
                          @ended="onPlaybackEnded(currentFullScreenSubmission?.id)"
                        ></audio>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- フィードバック録音機能 -->
                <div class="feedback-section feedback-recording-section">
                  <div class="feedback-recording-section">
                    
                    <div v-if="canShowStartButton" class="recording-start">
                      <button @click="startRecording" class="btn btn-primary large" :disabled="!canStartNow">
                        🎙️ 録音開始
                      </button>
                  </div>

                  <div v-if="isRecording" class="recording-active">
                    <div class="recording-indicator">
                      <span class="recording-dot"></span>
                      <span>録音中...</span>
                    </div>
                    <div class="recording-time">{{ formatRecordingTime(recordingTime) }}</div>
                    <button 
                      @click="stopRecording" 
                      class="btn btn-danger"
                      :disabled="!canStopRecording"
                      :class="{ 'btn-disabled': !canStopRecording }"
                    >
                      {{ canStopRecording ? '⏹️ 録音停止' : `⏹️ 録音停止 (${Math.max(0, 3 - recordingTime)}秒後)` }}
                    </button>
                  </div>

                  <!-- アップロード進捗表示 -->
                  <div v-if="isUploading" class="upload-progress-section">
                    <div class="upload-indicator">
                      <span class="upload-spinner">📤</span>
                      <span>アップロード中...</span>
                    </div>
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: uploadProgress.percentage + '%' }"></div>
                    </div>
                    <div class="progress-text">
                      {{ uploadProgress.completed }} / {{ uploadProgress.total }} チャンク
                    </div>
                  </div>

                  <!-- 録音完了後の音声プレイヤーと操作ボタン -->
                  <div v-if="isCompleted && !isRecording && !isUploading" class="recording-completed">
                    <div class="completed-message">
                      <span class="success-icon">✅</span>
                      <span>録音が完了しました</span>
                    </div>
                    
                    <!-- 音声プレイヤー -->
                    <div v-if="finalAudioUrl" class="audio-player-section">
                      <audio 
                        :src="finalAudioUrl" 
                        controls 
                        preload="metadata"
                        class="feedback-audio-player"
                        controlslist="nodownload noplaybackrate"
                        playsinline
                      >
                        お使いのブラウザは音声再生に対応していません。
                      </audio>
                    </div>
                    
                    <div class="recording-actions">
                      <button 
                        @click="retakeRecording" 
                        class="btn btn-secondary"
                        :disabled="isDeleting"
                      >
                        {{ isDeleting ? '🗑️ 削除中...' : '🔄 撮り直し' }}
                      </button>
                      <button @click="handleSendFeedback" class="btn btn-success" :disabled="isDeleting">
                        📤 送信
                      </button>
                    </div>
                  </div>
                  
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
    </Transition>
  </div>
  </InstructorLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, onBeforeRouteLeave } from 'vue-router'
import { supabase } from '../../supabaseClient'
import { useBuddySystem } from '../../composables/useBuddySystem.js'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import { 
  players,
  loadingId,
  setAudioRef,
  refreshUrl,
  maybeSeekRefresh,
  stopPlayback,
  onPlaybackEnded,
  cleanupAllPlayers,
  prefetchPlaybackUrl,
  ensureTimer
} from '../../composables/useButtonTriggerJit.js'
import { useFeedbackRecorder } from '../../composables/useFeedbackRecorder.js'
import { isVideoFeedbackSupported } from '../../utils/videoRecorder.js'

const router = useRouter()

// Buddy System導入
const buddy = useBuddySystem()

// 表示スコープのトグル
const helpOn = ref(false)      // バディ提出も含める
const showPool = ref(false)    // プール提出を見る

// 取得期間の設定（将来UI化可能）
const HOURS_WINDOW = 72

// 時計（1分ごと更新）
const now = ref(new Date())
let nowTimer = null
let nowTimerStart = null

// UI凍結フラグ（録音・モーダル中は時計を止める）
const isUiFrozen = computed(() =>
  showFeedbackModal.value || showFullScreenFeedback.value || isRecording.value || isUploading.value
)

// 認証トークン取得
async function getToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || ''
}

// モーダル表示中の重複DOM排除（未使用だがコメントアウト保持）
// const isModalFor = (id) =>
//   showFullScreenFeedback.value && currentFullScreenSubmission.value?.id === id

// 署名URLテスト関数
const testSignedUrl = (id) => {
  const player = players[id]
  if (!player?.url) {
    alert('署名URLが準備されていません')
    return
  }
  
  console.log('🔗 署名URLテスト:', {
    id,
    url: player.url,
    expiresAt: new Date(player.expiresAt).toLocaleString(),
    expiresIn: Math.round((player.expiresAt - Date.now()) / 1000) + '秒後'
  })
  
  // 新しいタブで署名URLを開いて直接テスト
  window.open(player.url, '_blank')
}

// 状態管理
const isLoading = ref(false)
const submissions = ref([])
const statusFilter = ref('') // フィルタなしで全データ表示
const dayFilter = ref('')
const searchQuery = ref('')
const selectedFilterType = ref('') // 選択されたフィルター種類
const viewMode = ref('submissions') // 'submissions' または 'students'
const isSubmitting = ref(false) // 二重提出防止

// 生徒別ビュー用：practiceデータの遅延読み込み
const studentPracticeData = ref(new Map()) // key: user_id, value: practice配列
const loadingPracticeForStudent = ref(new Set()) // 現在読み込み中のuser_id

// コンポーネントアンマウント時のクリーンアップ
onUnmounted(() => {
  cleanupAllPlayers()
  cleanupRecorder()
  if (nowTimer) clearInterval(nowTimer)
  if (nowTimerStart) clearTimeout(nowTimerStart)
})

// ルート離脱時のクリーンアップ（保険）
onBeforeRouteLeave(() => {
  if (!canCloseModal.value) {
    // 進行中は遷移を止める
    console.warn('操作中のため、ページ遷移ができません')
    return false
  }
  // 進行中でなければクリーンアップ（既存処理）
  cleanupAllPlayers()
  cleanupRecorder()
})
const expandedStudents = ref([]) // 展開されている生徒のIDリスト
const expandedDays = ref([]) // 展開されているDayのIDリスト（studentId-day形式）

// フィードバック録音関連
const showFeedbackModal = ref(false)
const currentSubmission = ref(null)
const feedbackAudio = ref(null)

// 新しいチャンク録音システム
const {
  isRecording,
  isUploading,
  isCompleted,
  canStopRecording,
  isDeleting,
  recordingTime,
  uploadProgress,
  currentRecording,
  finalAudioUrl,
  previewStream,
  isPreviewReady,
  previewRotationDeg,
  prepareVideoPreview,
  rotatePreview,
  cancelVideoPreview,
  startRecording: startFeedbackRecording,
  stopRecording: stopFeedbackRecording,
  cancelRecording: cancelFeedbackRecording,
  retakeRecording,
  sendToLine,
  formatTime: formatRecordingTime,
  resetState: resetFeedbackState,
  cleanup: cleanupRecorder,
  cleanupRecordingData
} = useFeedbackRecorder()

// 動画録画中のライブプレビュー（自撮り確認用。生カメラstreamをそのまま表示するだけ）
// stream準備完了とプレビュー要素マウントの順序が前後するため、関数refとwatch両方でsrcObjectを設定する
const previewVideoEl = ref(null)
function setPreviewVideoEl(el) {
  previewVideoEl.value = el
  // Vueはこの関数refを再描画のたびに呼び直すため、同じstreamへの再代入による
  // ちらつき（黒く点滅）を防ぐためすでに同じstreamなら何もしない
  if (el && el.srcObject !== (previewStream.value || null)) {
    el.srcObject = previewStream.value || null
  }
}
// 生ストリームは向きの補正をしていないため、プレビュー表示だけCSSで回転させる
// （実際に保存される映像の回転はサーバー側のffmpegが確定角度を使って行う）
const previewVideoStyle = computed(() => {
  const deg = previewRotationDeg.value
  const rotated = deg === 90 || deg === 270
  return {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: rotated ? '133.333%' : '100%',
    height: rotated ? '75%' : '100%',
    objectFit: 'cover',
    transform: `translate(-50%, -50%) rotate(${deg}deg) scaleX(-1)`
  }
})
watch(previewStream, (stream) => {
  if (previewVideoEl.value && previewVideoEl.value.srcObject !== (stream || null)) {
    previewVideoEl.value.srcObject = stream || null
  }
})

const isPreparingPreview = ref(false)
// 「カメラ準備」ボタン: 録画開始前にカメラプレビューを準備する
async function onPrepareVideoPreview() {
  if (!currentSubmission.value) return
  isPreparingPreview.value = true
  try {
    await prepareVideoPreview(currentSubmission.value)
  } catch {
    // エラー内容はprepareVideoPreview内でalert済み
  } finally {
    isPreparingPreview.value = false
  }
}
// 「向きを直す」ボタン: プレビューを90度ずつ回転（保存も内部で行う）
function onRotatePreview() {
  rotatePreview()
}
// プレビュー確認後の「録画開始」ボタン
async function onStartVideoRecording() {
  await startRecording()
}
// プレビューの「キャンセル」ボタン
function onCancelVideoPreview() {
  cancelVideoPreview()
}

// 全画面フィードバック関連
const showFullScreenFeedback = ref(false)
const currentFullScreenSubmission = ref(null)

// 統計情報の計算（表示データを対象）
const totalSubmissions = computed(() => displaySubmissions.value.length)
const pendingFeedback = computed(() => 
  displaySubmissions.value.filter(s => s.status === 'submitted').length
)
const completedFeedback = computed(() => 
  displaySubmissions.value.filter(s => s.status === 'feedback_completed').length
)
const activeStudents = computed(() => 
  new Set(displaySubmissions.value.map(s => s.user_id)).size
)

// アクティブなフィルターがあるかチェック
const hasActiveFilters = computed(() => {
  return statusFilter.value || dayFilter.value || searchQuery.value
})

// 生徒別ビュー用のデータ（スパース生成）
const studentsData = computed(() => {
  // まず現在の displaySubmissions を user_id でグルーピング
  const byStudent = new Map()
  for (const s of displaySubmissions.value) {
    if (!byStudent.has(s.user_id)) {
      byStudent.set(s.user_id, {
        user_id: s.user_id,
        student_name: s.student_name || '未設定',
        submissions: [],
        unreadCount: 0,
        oldestUnreadTime: null,
      })
    }
    const bucket = byStudent.get(s.user_id)
    bucket.submissions.push(s)
    if (s.status === 'submitted') {
      bucket.unreadCount++
      const t = new Date(s.created_at)
      if (!bucket.oldestUnreadTime || t < bucket.oldestUnreadTime) bucket.oldestUnreadTime = t
    }
  }

  // Day単位のスパース化（assignment + practice）
  for (const student of byStudent.values()) {
    const byDay = new Map()
    
    // まず assignment を追加
    for (const s of student.submissions) {
      const key = s.day
      if (!byDay.has(key)) {
        byDay.set(key, {
          day: s.day,
          practiceSubmissions: [],
          assignmentSubmissions: [],
          hasUnread: false,
          unreadCount: 0,
          oldestUnreadTime: null
        })
      }
      const slot = byDay.get(key)
      slot.assignmentSubmissions.push(s)

      if (s.status === 'submitted') {
        slot.hasUnread = true
        slot.unreadCount++
        const t = new Date(s.created_at)
        if (!slot.oldestUnreadTime || t < slot.oldestUnreadTime) slot.oldestUnreadTime = t
      }
    }
    
    // 🆕 キャッシュから practice を追加
    const practices = studentPracticeData.value.get(student.user_id) || []
    for (const p of practices) {
      const key = p.day
      if (!byDay.has(key)) {
        byDay.set(key, {
          day: p.day,
          practiceSubmissions: [],
          assignmentSubmissions: [],
          hasUnread: false,
          unreadCount: 0,
          oldestUnreadTime: null
        })
      }
      const slot = byDay.get(key)
      slot.practiceSubmissions.push(p)
    }
    
    // 表示は Day 降順
    student.daysData = Array.from(byDay.values()).sort((a,b) => b.day - a.day)
  }

  return Array.from(byStudent.values())
})

// 経過時間を計算（提出からの経過）
const getElapsedTime = (createdAt) => {
  if (!createdAt) return ''
  const diffMs = now.value - new Date(createdAt)
  if (diffMs < 0) return '0分'  // 未来なら0扱い
  const mins = Math.floor(diffMs / 60000)
  const h = Math.floor(mins / 60), m = mins % 60
  return h > 0 ? `${h}時${m}分` : `${m}分`
}

// JSTのDate作成ヘルパー（Safari厳密対応）
const makeJSTDate = (d) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(d)
  
  const get = (type) => parts.find(p => p.type === type)?.value
  return new Date(`${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`)
}

// 14:00締切までの残り時間を計算（JST基準・Safari厳密対応）
const getDeadlineRemaining = (createdAt) => {
  if (!createdAt) return null
  
  // 提出日時をJSTで取得
  const submittedAt = new Date(createdAt)
  const jstSubmittedAt = makeJSTDate(submittedAt)
  
  // 提出日の翌日14:00をJSTで計算
  const deadlineDate = new Date(jstSubmittedAt)
  deadlineDate.setDate(deadlineDate.getDate() + 1)
  deadlineDate.setHours(14, 0, 0, 0)
  
  // 現在時刻をJSTで取得
  const jstNow = makeJSTDate(now.value)
  
  const diffMs = deadlineDate.getTime() - jstNow.getTime()
  const diffMinutes = Math.floor(diffMs / (1000 * 60))
  
  const isOverdue = diffMs <= 0
  const absMinutes = Math.abs(diffMinutes)
  const hours = Math.floor(absMinutes / 60)
  const minutes = absMinutes % 60
  const timeText = hours > 0 ? `${hours}時${minutes}分` : `${minutes}分`
  
  return {
    isOverdue,
    text: isOverdue ? `-${timeText}` : `+${timeText}`,
    cssClass: isOverdue ? 'overdue' : 'safe',
    diffMs  // ソート用（負の値=過ぎている、正の値=残り時間）
  }
}

// フィードバック期限を計算（旧関数・後方互換性）
const getFeedbackDeadline = (oldestUnreadTime) => {
  if (!oldestUnreadTime) return null
  const deadline = getDeadlineRemaining(oldestUnreadTime)
  return deadline ? deadline.text : null
}

// フィルタリングされた提出課題
const filteredSubmissions = computed(() => {
  let filtered = submissions.value

  if (statusFilter.value) {
    filtered = filtered.filter(s => s.status === statusFilter.value)
  }

  if (dayFilter.value) {
    filtered = filtered.filter(s => s.day === parseInt(dayFilter.value))
  }

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(s => 
      (s.student_name && s.student_name.toLowerCase().includes(query)) ||
      String(s.user_id || '').toLowerCase().includes(query)
    )
  }

  return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
})

// 優先度計算関数
const calculatePriority = (submission) => {
  // 第1優先：担当 > buddy > pool
  let typePriority = 0
  if (submission.__badge_assigned) typePriority = 3
  else if (submission.__badge_buddy) typePriority = 2
  else if (submission.__badge_pool) typePriority = 1
  
  // 第2優先：締切までの残り時間（小さいほど優先、負の値=過ぎている）
  const deadline = getDeadlineRemaining(submission.created_at)
  const deadlineMs = deadline?.diffMs ?? Infinity
  
  return {
    typePriority,
    deadlineMs
  }
}

// 優先順位ソート関数
const sortByPriority = (a, b) => {
  const priorityA = calculatePriority(a)
  const priorityB = calculatePriority(b)
  
  // まず種類優先度で比較（担当 > buddy > pool）
  if (priorityA.typePriority !== priorityB.typePriority) {
    return priorityB.typePriority - priorityA.typePriority
  }
  
  // 次に締切で比較（近いほど優先、過ぎているものが最優先）
  return priorityA.deadlineMs - priorityB.deadlineMs
}

// 表示用データ（Pool選択時は優先順位ソート＋100件制限）
const displaySubmissions = computed(() => {
  if (!showPool.value) {
    // Pool未選択：全件表示
    return filteredSubmissions.value
  } else {
    // Pool選択：優先順位ソート → 上位100件
    const sorted = [...filteredSubmissions.value].sort(sortByPriority)
    return sorted.slice(0, 100)
  }
})

// フィルタ実行
const applyFilters = () => {
  // フィルタが適用された状態でデータを再計算
  
  // フィルタ更新時は一旦全プレイヤー停止（シンプルで安全）
  cleanupAllPlayers()
  
  // フィルタ適用後、生徒別ビューの展開状態をリセット
  if (viewMode.value === 'students') {
    expandedStudents.value = []
    expandedDays.value = []
  }
}

// フィルタ全解除
const clearAllFilters = () => {
  selectedFilterType.value = ''
  statusFilter.value = ''
  dayFilter.value = ''
  searchQuery.value = ''
  console.log('🗑️ フィルタを全解除しました')
  
  // フィルタ解除後、生徒別ビューの展開状態をリセット
  if (viewMode.value === 'students') {
    expandedStudents.value = []
    expandedDays.value = []
  }
}

onMounted(async () => {
  // Buddy情報の初期化
  await buddy.initialize()
  await loadSubmissionsScoped()   // 以降は"最適化版"で取得
  
  // ESCキーでのモーダル閉じをガード
  const onKeydown = (e) => {
    if (e.key === 'Escape') {
      if (!canCloseModal.value) {
        e.preventDefault()
        e.stopPropagation()
        console.warn('操作中のため、ESCキーでモーダルを閉じることができません')
      } else {
        // どちらか開いている方を閉じる
        if (showFullScreenFeedback.value) closeFullScreenFeedback()
        else if (showFeedbackModal.value) closeFeedbackModal()
      }
    }
  }
  window.addEventListener('keydown', onKeydown)
  
  // ブラウザのリロード/タブ閉じをガード
  const onBeforeUnload = (e) => {
    if (!canCloseModal.value && (showFeedbackModal.value || showFullScreenFeedback.value)) {
      e.preventDefault()
      e.returnValue = '' // Chrome系で必要
      console.warn('操作中のため、ページを離れることができません')
    }
  }
  window.addEventListener('beforeunload', onBeforeUnload)
  
  // 後片付け
  onUnmounted(() => {
    window.removeEventListener('keydown', onKeydown)
    window.removeEventListener('beforeunload', onBeforeUnload)
  })
  
  // 1分ごとの時計（録音・モーダル中は凍結）
  const tick = () => { if (!isUiFrozen.value) now.value = new Date() }
  // 分頭までの残りmsを計算して1回だけ合わせる
  const msToNextMinute = 60000 - (Date.now() % 60000)
  nowTimerStart = setTimeout(() => {
    tick()
    nowTimer = setInterval(tick, 60000)
  }, msToNextMinute)
})

// 最適化版：72時間＋担当/HELP/Poolの"最小データ"取得（assignmentのみ）
const loadSubmissionsScoped = async () => {
  isLoading.value = true
  try {
    // 72h の下限（定数化）
    const sinceIso = new Date(Date.now() - HOURS_WINDOW * 60 * 60 * 1000).toISOString()

    // 1) 自分の担当生徒（常に含める）
    const assignedLearnerIds = buddy.instructorAssignments.value.map(a => a.learner_user_id)

    // 2) HELPオン時のみ、同グループのバディ対象も含める
    let buddyLearnerIds = []
    if (helpOn.value) {
      buddyLearnerIds = await buddy.getBuddyLearnerIds(buddy.currentInstructor.value.id)
    }

    // 3) Pool表示なら "全体" を見る。そうでなければ assigned ∪ buddy
    const userIdFilter = showPool.value ? null : [...new Set([...assignedLearnerIds, ...buddyLearnerIds])]
    
    // 4) Supabase クエリ - assignment のみ取得
    let assignmentQuery = supabase
      .from('submissions_v3')
      .select('id, user_id, day, type, status, duration, created_at')
      .eq('type', 'assignment')  // assignmentのみ
      .gte('created_at', sinceIso)
      .order('created_at', { ascending: false })

    if (!showPool.value) {
      // Pool未選択：担当＋バディのみ、1000件まで
      if (userIdFilter.length === 0) {
        submissions.value = []
        return
      }
      assignmentQuery = assignmentQuery.in('user_id', userIdFilter).limit(1000)
    } else {
      // Pool選択：全体から500件取得（優先順位ソート後100件表示）
      assignmentQuery = assignmentQuery.limit(500)
    }

    const { data: assignmentData, error: assignmentError } = await assignmentQuery
    if (assignmentError) throw assignmentError
    
    // 4-2) learners テーブルから学習者情報を取得
    let learnersData = []
    if (assignmentData && assignmentData.length > 0) {
      const parentIds = [...new Set(assignmentData.map(a => a.user_id))]
      
      const { data: learnersResult, error: learnersError } = await supabase
        .from('learners')
        .select('parent_id, name, name_kana')
        .in('parent_id', parentIds)
      
      if (learnersError) {
        console.warn('⚠️ 学習者情報取得エラー:', learnersError)
      } else {
        learnersData = learnersResult || []
      }
    }
    
    // learners を Map 化（parent_id でマッピング）
    const learnersMap = new Map(learnersData?.map(l => [l.parent_id, l]) ?? [])

    // 5) 関連する practice データを取得
    let practiceData = []
    if (assignmentData && assignmentData.length > 0) {
      const assignmentDays = [...new Set(assignmentData.map(a => a.day))]
      const assignmentUserIds = [...new Set(assignmentData.map(a => a.user_id))]

      let practiceQuery = supabase
        .from('submissions_v3')
        .select('id, user_id, day, duration, created_at')
        .eq('type', 'practice')
        .in('day', assignmentDays)
        .in('user_id', assignmentUserIds)
        .gte('created_at', sinceIso)

      const { data: practiceResult, error: practiceError } = await practiceQuery
      
      if (practiceError) {
        console.warn('⚠️ 練習データ取得エラー:', practiceError)
      } else {
        practiceData = practiceResult || []
      }
    }

    // 6) practice を user_id + day でグループ化
    const practiceMap = new Map()
    practiceData.forEach(p => {
      const key = `${p.user_id}-${p.day}`
      if (!practiceMap.has(key)) {
        practiceMap.set(key, [])
      }
      practiceMap.get(key).push(p)
    })

    // 7) 表示用フィールド付与（バッジ用 + practice情報）
    const assignedSet = new Set(assignedLearnerIds)
    const buddySet    = new Set(buddyLearnerIds)

    submissions.value = (assignmentData || []).map(s => {
      const key = `${s.user_id}-${s.day}`
      const practices = practiceMap.get(key) || []
      const learner = learnersMap.get(s.user_id)
      
      return {
        ...s,
        student_name: learner?.name || '不明',
        student_name_kana: learner?.name_kana || '',
        __practice_count: practices.length,
        __practice_total_duration: practices.reduce((sum, p) => sum + (p.duration || 0), 0),
        __has_practice: practices.length > 0,
        __badge_assigned: assignedSet.has(s.user_id),
        __badge_buddy:    buddySet.has(s.user_id),
        __badge_pool:     showPool.value && !assignedSet.has(s.user_id) && !buddySet.has(s.user_id),
      }
    })
    
    console.log(`✅ データ取得完了:`)
    console.log(`  - assignment読み込み: ${assignmentData?.length || 0}件`)
    console.log(`  - learners読み込み: ${learnersData.length}件`)
    console.log(`  - practice読み込み: ${practiceData.length}件`)
    console.log(`  - Pool選択: ${showPool.value ? 'ON (500件limit)' : 'OFF (担当＋バディのみ)'}`)
  } catch (e) {
    console.error('❌ 提出データ読み込みエラー:', e)
    alert('データの読み込みに失敗しました')
  } finally {
    isLoading.value = false
  }
}

// ビューモード切り替え
const setViewMode = (mode) => {
  if (viewMode.value !== mode) {
    cleanupAllPlayers()
    expandedStudents.value = []
    expandedDays.value = []
    // 🆕 ビュー切り替え時もpracticeキャッシュをクリア
    studentPracticeData.value.clear()
    viewMode.value = mode
  }
}

// 特定の生徒のpracticeデータを取得（遅延読み込み）
const loadStudentPractice = async (userId) => {
  // 既にキャッシュがあればスキップ
  if (studentPracticeData.value.has(userId)) {
    console.log(`📦 practiceキャッシュ利用: ${userId}`)
    return
  }
  
  // 重複読み込み防止
  if (loadingPracticeForStudent.value.has(userId)) {
    console.log(`⏳ practice読み込み中: ${userId}`)
    return
  }
  
  loadingPracticeForStudent.value.add(userId)
  
  try {
    const sinceIso = new Date(Date.now() - HOURS_WINDOW * 60 * 60 * 1000).toISOString()
    
    const { data, error } = await supabase
      .from('submissions_v3')
      .select('id, user_id, day, type, status, duration, created_at')
      .eq('type', 'practice')
      .eq('user_id', userId)
      .gte('created_at', sinceIso)
      .order('day', { ascending: false })
    
    if (error) throw error
    
    // キャッシュに保存
    studentPracticeData.value.set(userId, data || [])
    console.log(`✅ practice読み込み完了: ${userId} (${data?.length || 0}件)`)
    
  } catch (e) {
    console.error('❌ practice読み込みエラー:', e)
    // エラー時も空配列をキャッシュ（無限リトライ防止）
    studentPracticeData.value.set(userId, [])
  } finally {
    loadingPracticeForStudent.value.delete(userId)
  }
}

// 生徒の蛇腹を開閉
const toggleStudent = async (userId) => {
  const index = expandedStudents.value.indexOf(userId)
  if (index > -1) {
    expandedStudents.value.splice(index, 1)
    // 生徒を閉じる時、その生徒のDayも全て閉じる
    expandedDays.value = expandedDays.value.filter(dayId => !dayId.startsWith(`${userId}-`))
  } else {
    expandedStudents.value.push(userId)
    
    // 🆕 この生徒のpracticeデータを取得（遅延読み込み）
    await loadStudentPractice(userId)
  }
}

// Dayの蛇腹を開閉
const toggleDay = (userId, day) => {
  const dayId = `${userId}-${day}`
  const index = expandedDays.value.indexOf(dayId)
  if (index > -1) {
    expandedDays.value.splice(index, 1)
  } else {
    expandedDays.value.push(dayId)
  }
}

// データ更新（モーダル操作中は実行しない安全策）
const refreshData = async () => {
  if (!canCloseModal.value) return
  cleanupAllPlayers()
  // 🆕 practiceキャッシュをクリア
  studentPracticeData.value.clear()
  await loadSubmissionsScoped()
}

// トグル変更時に再取得
const onScopeToggle = async () => {
  // 🆕 practiceキャッシュをクリア
  studentPracticeData.value.clear()
  await refreshData()
}



// ログアウト
const handleLogout = async () => {
  try {
    await supabase.auth.signOut()
    router.push('/instructor/login')
  } catch (error) {
    console.error('ログアウトエラー:', error)
  }
}

// 通知設定ページに遷移
const goToNotificationSettings = () => {
  router.push('/instructor/notification-settings')
}

// フィードバック開始
const startFeedback = (submission) => {
  resetFeedbackState()
  currentSubmission.value = submission
  showFeedbackModal.value = true
  feedbackAudio.value = null
}

// フィードバック続行
const continueFeedback = (submission) => {
  // 既存のフィードバック音声を読み込む
  resetFeedbackState()
  currentSubmission.value = submission
  showFeedbackModal.value = true
  // TODO: 既存のフィードバック音声を読み込む処理
}

// フィードバック確認
const viewFeedback = (submission) => {
  // TODO: フィードバック詳細表示
  console.log('フィードバック確認:', submission)
}

// 全画面フィードバック開始
const openFullScreenFeedback = async (submission) => {
  resetFeedbackState()
  currentFullScreenSubmission.value = submission
  try {
    // ここで先に署名URLだけ取得（モーダル表示時点でaudio.srcが埋まる）
    console.log('🔗 プリフェッチ開始:', submission.id)
    await prefetchPlaybackUrl(submission.id)
  } catch (e) {
    console.error('prefetch failed:', e)
    // 失敗してもモーダルは開く（中で「再取得」できるようにする）
  } finally {
    showFullScreenFeedback.value = true
    await nextTick()
    // タイマーは音声再生開始時に設定（プリフェッチ直後は不要）
    // ensureTimer(submission.id) // ← 削除：即座のリフレッシュを防ぐ
  }
}

// 全画面フィードバック終了
const closeFullScreenFeedback = async () => {
  // 操作中・動作中は閉じない
  if (!canCloseModal.value) {
    console.warn('録音中またはアップロード中のため、モーダルを閉じることができません')
    return
  }

  const id = currentFullScreenSubmission.value?.id
  const recordingId = currentRecording?.value?.recordingId
  if (id) stopPlayback(id)

  // 録音状態もリセット（await で安全に停止）
  if (isRecording.value) {
    await stopFeedbackRecording()
  }

  // 未送信・未撮り直しで閉じる場合: IndexedDBの該当録音データを破棄
  if (recordingId) {
    await cleanupRecordingData(recordingId)
  }

  showFullScreenFeedback.value = false
  currentFullScreenSubmission.value = null
  feedbackAudio.value = null
  resetFeedbackState()
}

// 全画面フィードバック提出（未使用：チャンク録音システムに置き換え済み）
/*
const submitFullScreenFeedback = async () => {
  if (!currentFullScreenSubmission.value || !feedbackAudio.value) return
  
  try {
    isSubmitting.value = true
    // 既存のsubmitFeedback関数と同じ処理を実行
    const response = await fetch(feedbackAudio.value)
    const audioBlob = await response.blob()
    
    const formData = new FormData()
    formData.append('audio', audioBlob, `feedback_${currentFullScreenSubmission.value.day}_${currentFullScreenSubmission.value.type}.webm`)
    formData.append('submission_id', currentFullScreenSubmission.value.id)
    formData.append('user_id', currentFullScreenSubmission.value.user_id)
    formData.append('day', currentFullScreenSubmission.value.day)
    formData.append('type', currentFullScreenSubmission.value.type)
    formData.append('duration', recordingTime.value)
    
    const uploadResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/upload-feedback`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${await getToken()}` 
      },
      body: formData
    })
    
    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json()
      throw new Error(errorData.message || 'アップロードに失敗しました')
    }
    
    const result = await uploadResponse.json()
    console.log('✅ 全画面フィードバック提出完了:', result)
    
    // モーダルを閉じる
    closeFullScreenFeedback()
    
    // データを更新
    await refreshData()
    
    alert('フィードバックが提出されました！')
  } catch (error) {
    console.error('全画面フィードバック提出エラー:', error)
    alert(`フィードバックの提出に失敗しました: ${error.message}`)
  } finally {
    isSubmitting.value = false
  }
}
*/

// 録音開始ボタンの表示・有効化ガード条件
const canShowStartButton = computed(() => {
  return !isRecording.value && !isUploading.value && !isCompleted.value && !finalAudioUrl.value
})

const canStartNow = computed(() => canShowStartButton.value) // 将来の条件追加にも対応

// フィードバック録音モード（音声/動画）。動画はAndroid/PCのみ対応
const isVideoSupported = isVideoFeedbackSupported()
const selectedMediaKind = ref(isVideoSupported ? 'video' : 'audio')

// モーダル閉じ禁止条件（操作中・動作中は閉じられない）
const canCloseModal = computed(() => {
  return !isRecording.value && !isUploading.value && !isDeleting.value && !isSubmitting.value
})

// 録音開始
const startRecording = async () => {
  // 二重起動ガード
  if (!canStartNow.value) {
    console.warn('録音開始条件が満たされていません')
    return
  }
  
  try {
    const submissionData = currentSubmission.value || currentFullScreenSubmission.value
    if (!submissionData) {
      console.error('提出データが見つかりません')
      return
    }
    
    await startFeedbackRecording(submissionData, selectedMediaKind.value)

  } catch (error) {
    console.error('フィードバック録音開始エラー:', error)
    alert(`録音開始エラー: ${error.message}`)
  }
}

// 録音停止（新しいチャンクシステム）
const stopRecording = async () => {
  try {
    await stopFeedbackRecording()
  } catch (error) {
    console.error('フィードバック録音停止エラー:', error)
    alert(`録音停止エラー: ${error.message}`)
  }
}

// 注意: reRecord関数は削除済み（retakeRecording関数に置き換え）

// フィードバック提出（未使用：チャンク録音システムに置き換え済み）
/*
const submitFeedback = async () => {
  if (!currentSubmission.value || !feedbackAudio.value) return
  
  try {
    isSubmitting.value = true
    // フィードバック音声をBlobに変換
    const response = await fetch(feedbackAudio.value)
    const audioBlob = await response.blob()
    
    // FormDataを作成
    const formData = new FormData()
    formData.append('audio', audioBlob, `feedback_${currentSubmission.value.day}_${currentSubmission.value.type}.webm`)
    formData.append('submission_id', currentSubmission.value.id)
    formData.append('user_id', currentSubmission.value.user_id)
    formData.append('day', currentSubmission.value.day)
    formData.append('type', currentSubmission.value.type)
    formData.append('duration', recordingTime.value)
    
    // フィードバック音声をアップロード
    const uploadResponse = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/upload-feedback`, {
      method: 'POST',
      headers: { 
        Authorization: `Bearer ${await getToken()}` 
      },
      body: formData
    })
    
    if (!uploadResponse.ok) {
      const errorData = await uploadResponse.json()
      throw new Error(errorData.message || 'アップロードに失敗しました')
    }
    
    const result = await uploadResponse.json()
    console.log('✅ フィードバック提出完了:', result)
    
    // モーダルを閉じる
    closeFeedbackModal()
    
    // データを更新
    await refreshData()
    
    alert('フィードバックが提出されました！')
  } catch (error) {
    console.error('フィードバック提出エラー:', error)
    alert(`フィードバックの提出に失敗しました: ${error.message}`)
  } finally {
    isSubmitting.value = false
  }
}
*/

// モーダルを閉じる
const closeFeedbackModal = async () => {
  // 操作中・動作中は閉じない
  if (!canCloseModal.value) {
    console.warn('録音中またはアップロード中のため、モーダルを閉じることができません')
    return
  }
  
  // 録音状態もリセット（await で安全に停止）
  if (isRecording.value) {
    await stopRecording()
  }
  
  showFeedbackModal.value = false
  currentSubmission.value = null
  feedbackAudio.value = null
  resetFeedbackState()
}

// フィードバック送信処理
const handleSendFeedback = async () => {
  try {
    console.log('📤 フィードバック送信処理開始')
    
    // sendToLine を実行
    const result = await sendToLine()
    
    console.log('✅ フィードバック送信完了:', result)
    
    // 成功メッセージ
    alert('フィードバックを送信しました！\n生徒のLINEに通知が届きます。')
    
    // モーダルを閉じる
    await closeFullScreenFeedback()
    
    // データを更新
    await refreshData()
    
  } catch (error) {
    console.error('❌ フィードバック送信処理エラー:', error)
    alert(`送信エラー: ${error.message}`)
  }
}

// ユーティリティ関数
const formatDate = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })
}

const formatDateTime = (dateString) => {
  if (!dateString) return ''
  return new Date(dateString).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })
}

const formatDuration = (seconds) => {
  if (!seconds) return '0:00'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B'
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

const getStatusText = (status) => {
  const statusMap = {
    'submitted': 'FB待ち',
    'feedback_completed': 'フィードバック完了',
    'no_reply_needed': '返信不要'
  }
  return statusMap[status] || status
}

// フィードバック完了済みかチェック
const isFeedbackCompleted = (submission) => {
  return submission.status === 'feedback_completed'
}

// 返信不要としてマーク
const markAsNoReply = async (submission) => {

  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/mark-no-reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await getToken()}`
      },
      body: JSON.stringify({
        submission_id: submission.id,
        user_id: submission.user_id,
        day: submission.day,
        type: submission.type
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '返信不要マークに失敗しました')
    }

    const result = await response.json()
    console.log('✅ 返信不要マーク完了:', result)
    
         // データを更新
     await refreshData()
   } catch (error) {
    console.error('返信不要マークエラー:', error)
    alert(`返信不要マークに失敗しました: ${error.message}`)
  }
}

// 返信不要解除
const unmarkAsNoReply = async (submission) => {

  try {
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'https://api.otoeigo.com'}/api/unmark-no-reply`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${await getToken()}`
      },
      body: JSON.stringify({
        submission_id: submission.id,
        user_id: submission.user_id,
        day: submission.day,
        type: submission.type
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.message || '返信不要解除に失敗しました')
    }

    const result = await response.json()
    console.log('✅ 返信不要解除完了:', result)
    
         // データを更新
     await refreshData()
   } catch (error) {
    console.error('返信不要解除エラー:', error)
    alert(`返信不要解除に失敗しました: ${error.message}`)
  }
}
</script>

<style scoped>
.instructor-dashboard {
  width: 100%;
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.dashboard-header h1 {
  color: #1f2937;
  margin: 0;
  font-size: 2rem;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.stats-fixed {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
}

.stat-card-fixed {
  background: #667eea;
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  text-align: center;
  min-width: 80px;
}

.stat-number {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.9;
}

.filters-section {
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.filter-row {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.filter-type-selector,
.status-selector {
  flex: 1;
}

.clear-filter-btn {
  flex-shrink: 0;
}

.btn-clear {
  background: #6b7280;
  color: white;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  min-width: 60px;
}

.filter-value-selector {
  margin-bottom: 1rem;
}

.filter-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-input-group label {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

.filter-actions {
  text-align: center;
}

.form-select, .search-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  box-sizing: border-box;
}

/* 音声プレーヤー（ボタントリガーJIT方式） */
.submitted-audio-section {
  padding: 0;
}

.audio-slot {
  margin: 0;
}

.audio-player-section {
  margin: 0;
}

.inline-audio-player {
  width: 100%;
  height: 40px;
  margin-bottom: 0.5rem;
}

.player-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.player-actions .btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
}

.no-audio {
  margin: 0.75rem 0;
  padding: 0.5rem;
  background: #f3f4f6;
  border-radius: 6px;
  text-align: center;
}

.no-audio-text {
  color: #6b7280;
  font-size: 0.8rem;
  font-style: italic;
}

.submissions-section h2 {
  color: #1f2937;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

/* Pool表示情報 */
.pool-info {
  background: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
}

.pool-info .info-text {
  margin: 0;
  color: #1e40af;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
}

.submissions-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.submission-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-left: 4px solid #e5e7eb;
  text-align: left;
}

.submission-card.needs-feedback {
  border-left-color: #f59e0b;
  background: #fffbeb;
}

.submission-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
  position: relative;
}

.submission-header-right {
  position: absolute;
  top: 0;
  right: 0;
}

.btn-fb {
  background: #10b981;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  border-radius: 6px;
}

.student-name-section {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.student-info h3 {
  margin: 0;
  color: #1f2937;
  font-size: 1.1rem;
}

.student-kana-small {
  font-weight: normal;
  color: #6b7280;
  font-size: 0.8rem;
}

.submission-date {
  color: #6b7280;
  font-size: 0.8rem;
}

.submission-meta {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.day-badge, .type-badge, .status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.day-badge {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.practice {
  background: #dcfce7;
  color: #166534;
}

.type-badge.assignment {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.submitted {
  background: #fef3c7;
  color: #92400e;
}


.status-badge.feedback_completed {
  background: #dcfce7;
  color: #166534;
}

.status-badge.no_reply_needed {
  background: #e5e7eb;
  color: #374151;
}

.submission-details {
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #6b7280;
  font-size: 0.9rem;
}

.detail-value {
  color: #374151;
  font-weight: 500;
  text-align: right;
}

/* practice 情報のスタイル */
.practice-info-row {
  background: #f9fafb;
  margin: 0.25rem 0;
  padding: 0.375rem 0.5rem !important;
  border-radius: 4px;
}

.practice-indicator {
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
}

.practice-indicator.has-practice {
  background: #dcfce7;
  color: #166534;
}

.practice-indicator.no-practice {
  background: #fef3c7;
  color: #92400e;
  font-style: italic;
}

/* 生徒別ビュー用のインライン表示 */
.practice-indicator-inline {
  display: block;
  padding: 0.25rem 0.5rem;
  margin-top: 0.25rem;
  background: #f3f4f6;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.practice-indicator-inline.has-practice {
  background: #dcfce7;
  color: #166534;
}

.practice-indicator-inline.no-practice {
  background: #fef3c7;
  color: #92400e;
  font-style: italic;
}

.submission-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn.small {
  padding: 0.375rem 0.75rem;
  font-size: 0.8rem;
}

.btn.large {
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-secondary {
  background: #6b7280;
  color: white;
}

.btn-success {
  background: #10b981;
  color: white;
}

.btn-warning {
  background: #f59e0b;
  color: white;
}

.btn-info {
  background: #0ea5e9;
  color: white;
}

.btn-danger {
  background: #ef4444;
  color: white;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.loading, .no-data {
  text-align: center;
  padding: 2rem;
  color: #6b7280;
}

/* フィードバックモーダル */
.feedback-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* 全画面フィードバックモーダル */
.fullscreen-feedback-modal {
  position: fixed;
  inset: 0;                    /* ← 画面全体・親でスクロールさせない */
  background: white;
  z-index: 2000;
  overflow: hidden;            /* ← ここでスクロールは子に委譲 */
  color-scheme: light;
  color: #1f2937;
}

/* 全画面モーダルアニメーション */
.fullscreen-modal-enter-active {
  transition: all 0.3s ease-out;
}

.fullscreen-modal-leave-active {
  transition: all 0.2s ease-in;
}

.fullscreen-modal-enter-from {
  transform: scale(0.8);
  opacity: 0;
}

.fullscreen-modal-leave-to {
  transform: scale(0.9);
  opacity: 0;
}

.fullscreen-modal-enter-to,
.fullscreen-modal-leave-from {
  transform: scale(1);
  opacity: 1;
}

.fullscreen-content {
  position: absolute;
  inset: 0;                    /* ← margin:1rem / calc() をやめて端まで */
  display: flex;
  flex-direction: column;
  background: #f8fafc;
  border: 0;                   /* ← 枠・角丸もモバイルは外すと見た目が自然 */
  border-radius: 0;
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000; /* 最前面に確実に配置 */
}

.close-btn:hover {
  background: #dc2626;
}

.close-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}

.close-btn:disabled:hover {
  background: #9ca3af;
}

.feedback-screen {
  flex: 1 1 auto;
  overflow-y: auto;             /* ← スクロールコンテナ */
  padding-bottom: 0 !important; /* ← 下の余白をゼロに */
  max-width: 1200px;
  margin: 0 auto;
}

.feedback-screen h2 {
  color: #1f2937;
  margin-bottom: 1rem;
  text-align: center;
}

/* 左上3段の情報表示：右上ボタンと重ならないよう右余白を確保 */
.feedback-header-mobile {
  padding: 0.5rem 72px 1rem 0.75rem; /* 右にボタン幅(40) + 余白分を確保 */
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f3f4f6;
}

.student-name-line {
  font-size: 1.3rem;
  font-weight: bold;
  color: #1f2937;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  text-align: left;
}

.student-kana-line {
  font-size: 1rem;
  color: #6b7280;
  line-height: 1.2;
  margin-bottom: 0.25rem;
  text-align: left;
}

.assignment-line {
  font-size: 1.1rem;
  font-weight: bold;
  color: #3b82f6;
  line-height: 1.2;
  text-align: left;
}

/* 画面幅が広いときも違和感なく */
@media (min-width: 769px) {
  .feedback-header-mobile {
    padding-right: 80px; /* 余白少し広めに */
  }
}

/* 旧ヘッダー（互換性のため残す） */
.feedback-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  margin-bottom: 2rem;
  border-bottom: 2px solid #f3f4f6;
}

.student-info {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.student-name {
  font-weight: bold;
  color: #1f2937;
  font-size: 1.1rem;
}

.student-kana {
  font-weight: normal;
  color: #6b7280;
  font-size: 0.9rem;
}

.assignment-info {
  font-weight: bold;
  color: #3b82f6;
  font-size: 1.1rem;
}

/* 全画面フィードバックセクション */
.feedback-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.feedback-section {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 音声・録音コントロールセクション（固定表示） */
.feedback-controls-section {
  position: sticky;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 1rem;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom)); /* ノッチ対応 */
  z-index: 2500;
  box-shadow: 0 -2px 8px rgba(0,0,0,0.1);
  margin-bottom: 0; /* 念のため */
}

/* 各フィードバックセクションの個別padding設定 */
.feedback-manual-section {
  padding: 1.5rem;
}

.feedback-assignment-section {
  padding: 1.5rem;
}

.feedback-audio-section {
  margin: 0;
  padding: 0;
  border: none;
  box-shadow: none;
}

.feedback-recording-section {
  margin: 0;
  padding: 0;
  border: none;
  box-shadow: none;
}

/* タブレット/PCでは従来の余白を活かす（任意） */
@media (min-width: 769px) {
  .fullscreen-content {
    inset: 1rem;            /* PCは余白ありのカード風でもOK */
    border: 2px solid #e5e7eb;
    border-radius: 12px;
  }
  .feedback-screen {
    padding-bottom: 0 !important; /* PC時も余白をゼロにしておく */
  }
}

.feedback-section h3 {
  margin: 0 0 1rem 0;
  color: #1f2937;
  font-size: 1.1rem;
  border-bottom: 2px solid #f3f4f6;
  padding-bottom: 0.5rem;
}

.manual-content ul {
  margin: 0;
  padding-left: 1.5rem;
  color: #374151;
  line-height: 1.6;
}

.manual-content li {
  margin-bottom: 0.5rem;
}

.assignment-content p {
  margin: 0.75rem 0;
  color: #374151;
  line-height: 1.5;
}

.fullscreen-audio-player {
  width: 100%;
  height: 50px;
  margin: 0;
}

.feedback-recording-section {
  text-align: center;
  margin: 0;
  padding: 0;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-content h3 {
  margin: 0 0 1.5rem 0;
  color: #1f2937;
  text-align: center;
}

.feedback-info {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.feedback-info p {
  margin: 0.5rem 0;
  color: #374151;
}

.recording-section {
  margin-bottom: 1.5rem;
}

.recording-start {
  text-align: center;
  margin: 0;
  padding: 0;
}

.media-kind-toggle {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.media-kind-toggle label {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
}

.media-kind-toggle label.is-disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.media-kind-note {
  width: 100%;
  font-size: 0.8rem;
  color: #888;
  margin: 0.25rem 0 0;
}

.recording-active {
  text-align: center;
}

.recording-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #ef4444;
  font-weight: 600;
}

.recording-dot {
  width: 12px;
  height: 12px;
  background: #ef4444;
  border-radius: 50%;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

.recording-time {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
}

.recording-preview {
  text-align: center;
}

.audio-player {
  width: 100%;
  margin-bottom: 1rem;
}

.recording-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

/* アップロード進捗スタイル */
.upload-progress-section {
  text-align: center;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  margin: 1rem 0;
}

.upload-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
}

.upload-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #1d4ed8);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.875rem;
  color: #6b7280;
}

/* 録音完了状態のスタイル */
.recording-completed {
  text-align: center;
  padding: 1rem;
  background: #f0fdf4;
  border-radius: 8px;
  border: 1px solid #bbf7d0;
  margin: 1rem 0;
}

.completed-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  font-weight: 600;
  color: #166534;
}

.success-icon {
  font-size: 1.2rem;
}

/* 音声プレイヤーセクション */
.audio-player-section {
  margin: 0;
  text-align: center;
}

.feedback-audio-player {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  border-radius: 8px;
  background: #f8fafc;
}

/* 撮影後のチェック。手元の録画は切り抜く前の映像なので、撮影中のプレビュー・サーバーの変換と同じく
   3:4の枠からはみ出る部分を中央で切って見せる（届く動画と同じ範囲になる。データ自体は変えない） */
.feedback-video-player {
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  object-fit: cover;
  margin: 0 auto;
  display: block;
  border-radius: 8px;
  background: #000;
}

/* 生ストリームをそのまま表示するため、向きに応じたサイズ・回転はJS側(previewVideoStyle)で
   インラインstyleとして指定する。ここでは固定の3:4の枠だけを用意する */
.preview-outer {
  position: relative;
  width: 100%;
  max-width: 280px;
  aspect-ratio: 3 / 4;
  margin: 0 auto 0.75rem;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}

.recording-preview {
  display: block;
}

.video-preview-section {
  text-align: center;
}

.rotate-btn {
  display: block;
  margin: 0 auto 0.75rem;
}

.preview-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

/* モバイル対応 */
@media (max-width: 768px) {
  .feedback-audio-player {
    max-width: 100%;
  }
}

/* 無効化ボタンのスタイル */
.btn-disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
  cursor: not-allowed !important;
  opacity: 0.6;
}

.btn-disabled:hover {
  background-color: #d1d5db !important;
  transform: none !important;
}

.modal-actions {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

/* ビューモード切り替えボタン */
.view-mode-toggle {
  display: flex;
  gap: 0.5rem;
  margin-right: 1rem;
}

.btn-mode {
  background: #e5e7eb;
  color: #374151;
  border: 2px solid transparent;
}

.btn-mode.active {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

/* 生徒別ビュー */
.students-section h2 {
  color: #1f2937;
  margin-bottom: 1rem;
  font-size: 1.5rem;
}

.students-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.student-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.student-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: #f9fafb;
  cursor: pointer;
  transition: background-color 0.2s;
}

.student-header:hover {
  background: #f3f4f6;
}

.student-info h3 {
  margin: 0 0 0.5rem 0;
  color: #1f2937;
  font-size: 1.2rem;
}

.student-status {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.unread-badge {
  background: #fef3c7;
  color: #92400e;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.unread-badge.small {
  padding: 0.125rem 0.375rem;
  font-size: 0.7rem;
}

/* ローディングバッジ */
.loading-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  animation: pulse-loading 1.5s ease-in-out infinite;
}

@keyframes pulse-loading {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* 締切バッジ（重複削除、下の統合版を使用） */

.student-toggle {
  color: #6b7280;
  font-size: 1.2rem;
}

.student-days {
  padding: 1rem;
  background: white;
}

.days-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.day-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  overflow: hidden;
}

.day-card.has-unread {
  border-color: #f59e0b;
  background: #fffbeb;
}

.day-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s;
}

.day-header:hover {
  background: #e5e7eb;
}

.day-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.day-number {
  font-weight: 600;
  color: #374151;
  font-size: 1rem;
}

.day-status {
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.day-toggle {
  color: #6b7280;
  font-size: 1rem;
}

.day-details {
  padding: 1rem;
  background: white;
  border-top: 1px solid #e5e7eb;
}

.submissions-detail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.submission-detail {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
  background: #f9fafb;
}

.submission-detail.practice {
  border-left: 4px solid #10b981;
}

.submission-detail.assignment {
  border-left: 4px solid #f59e0b;
}

.submission-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.type-label {
  color: #374151;
  font-weight: 600;
  font-size: 0.9rem;
}

.status {
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.8rem;
}

.status.submitted {
  background: #fef3c7;
  color: #92400e;
}

.status.feedback_completed {
  background: #dcfce7;
  color: #166534;
}

.status.no_reply_needed {
  background: #e5e7eb;
  color: #374151;
}

.status.not-submitted {
  color: #9ca3af;
}

.submission-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.info-item {
  color: #6b7280;
  font-size: 0.8rem;
}

/* 複数件の提出表示用スタイル */
.submission-count {
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
}

.submissions-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.submission-item {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  background: white;
}

.submission-item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.submission-number {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 600;
  background: #f3f4f6;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
}

.submission-item-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.submission-item-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.no-submissions {
  text-align: center;
  padding: 1rem;
  color: #9ca3af;
}



/* スマホ対応 */
@media (max-width: 768px) {
  .dashboard-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: center;
  }

  /* ヘッダー内ボタンの高さを統一（スマホ） */

  .header-actions .btn,
  .header-actions .btn-mode {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.9rem;
    height: 60px;            /* ← ここで高さを統一 */
    padding: 0 12px;         /* 横の余白だけにする */
    line-height: 1;          /* テキストの行高の影響を消す */
    border: 2px solid transparent; /* ← 全ボタンの枠幅を揃える（.btn-mode と一致） */
    box-sizing: border-box;  /* 枠を高さ計算に含める */
  }

  /* （任意）トグルを横並びで均等幅にしたい場合 */
  .view-mode-toggle {
    width: 100%;
    display: flex;
    gap: 0.5rem;
  }
  .view-mode-toggle .btn-mode {
    flex: 1;
  }


  
  .view-mode-toggle {
    margin-right: 0;
    margin-bottom: 1rem;
    justify-content: center;
  }
  
  .filters-section {
    flex-direction: column;
  }
  
  .filter-actions {
    margin-left: 0;
    justify-content: center;
    margin-top: 1rem;
  }
  
  .submission-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
  }
  
  .submission-meta {
    justify-content: flex-start;
  }
}

/* Buddy System制御のスタイル */
.buddy-toggles { 
  display: flex; 
  gap: 0.75rem; 
  align-items: center; 
}

.buddy-toggles .toggle { 
  display: flex; 
  gap: 0.375rem; 
  align-items: center; 
  font-weight: 600; 
}

/* バッジスタイル */
.badge-role {
  padding: 0.25rem 0.5rem; 
  border-radius: 12px; 
  font-size: 0.75rem; 
  font-weight: 700;
}

.badge-role.assigned { 
  background: #e0f2fe; 
  color: #075985; 
}

.badge-role.buddy { 
  background: #ecfccb; 
  color: #3f6212; 
}

.badge-role.pool { 
  background: #f3e8ff; 
  color: #6b21a8; 
}

/* 締切表示のスタイル */
.deadline-indicator {
  font-weight: 700;
}

.deadline-indicator.safe {
  color: #059669; /* 緑：まだ余裕 */
}

.deadline-indicator.overdue {
  color: #dc2626; /* 赤：期限超過 */
}

/* 締切バッジのスタイル */
.deadline-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
}

.deadline-badge.small {
  padding: 0.125rem 0.375rem;
  font-size: 0.7rem;
}

.deadline-badge.safe {
  background: #dcfce7;
  color: #166534;
}

.deadline-badge.overdue {
  background: #fee2e2;
  color: #dc2626;
}

/* モバイル向け微調整 */
@media (max-width: 768px) {
  .days-list {
    gap: 0.25rem;
  }
  
  .day-header {
    padding: 0.75rem;
  }
  
  .day-details {
    padding: 0.75rem;
  }
  
  .submission-detail {
    padding: 0.75rem;
  }
  
  .submission-item {
    padding: 0.5rem;
  }
  
  .submission-item-actions {
    justify-content: center;
  }
}

/* 全画面モーダルの「フィードバックの手順」を左寄せ（見出し＋本文） */
.fullscreen-feedback-modal .feedback-manual-section .manual-title {
  text-align: left !important;
}
.fullscreen-feedback-modal .feedback-manual-section .manual-content,
.fullscreen-feedback-modal .feedback-manual-section .manual-content .manual-steps,
.fullscreen-feedback-modal .feedback-manual-section .manual-content .manual-steps p {
  text-align: left !important;
  color: #374151;
}


</style>
